import { useNavigate } from 'react-router-dom'
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container'
import { IconName } from '../../../../shared/interfaces/icon-picker.model'
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading'
import AnalyticReportService from '../../../../shared/services/analytic-report/analytic-report.service'
import { useEffect, useState } from 'react'
import { IAnalyticReport } from '../../../../shared/services/analytic-report/analytic-report.model'
import { Box, Grid } from '@mui/material'
import { AnalyticReportCard } from '../../../presentation-components/analytics-components/reports/analytic-report-card'
import { LoadingPage } from '../../loading-page'
import { InformationBox } from '../../../presentation-components/miscellaneous-components/information-box'
import { useHasAReportPeriod } from '../../../../hooks/use-has-a-report-period'

// The saved report's filter dimensions (e.g. ["Group"]), for the card's chip row. The stored
// JSON is user data from an older shape in places, so any parse failure means no chips.
const parseFilterLabels = (reportJsonValue: string): string[] => {
  try {
    const parsed = JSON.parse(reportJsonValue);
    const labels = Array.isArray(parsed?.selectedFilters) ? parsed.selectedFilters : [];
    return labels.filter((label: unknown): label is string => typeof label === 'string' && label.length > 0);
  } catch {
    return [];
  }
};

export const AnalyticsReportsPage = () => {
  const navigate = useNavigate()
  //Services
  const analyticReportService = new AnalyticReportService()
  //State
  const [analyticReports, setAnalyticReports] = useState<IAnalyticReport[]>([])
  //Loading State
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Hooks
  const hasAReportPeriod = useHasAReportPeriod();
  
  useEffect(() => {
    analyticReportService.getAll()
      .then(response => {
        setAnalyticReports(response)
        setIsLoading(false)
      })
      .catch(err => {
        setIsLoading(false)
        console.log(err)
      })
  }, [])

  return (
    <PageOuterContainer>
      <PageHeading
        title='Analytics'
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => navigate('/analytics/reports/add-new-report')}
        actionButtonText='Add Analytics Report'
        hideShowActionButton={!hasAReportPeriod}
        hasTabs={false}
      />
      {hasAReportPeriod !== null && hasAReportPeriod === false && (
        <Box sx={{ mt: 2 }}>
          <InformationBox
            text='You need to create a report period before you can use Analytics Reports.'
            icon={IconName.ErrorOutline}
          />
        </Box>
      )}
      {hasAReportPeriod && (
        <LoadingPage pageReady={!isLoading}>
          {/* Halve the container's negative top margin (spacing 2 pulls it up 16px): the
              compact cards made the heading gap read tighter than sibling pages; this puts
              8px back without touching the gutters. */}
          <Grid container spacing={2} sx={{ mt: -1 }}>
            <>
              {analyticReports && analyticReports.length > 0 && analyticReports.map(report => {
                return (
                  <Grid key={report.id} item xl={4} lg={4} md={6} sm={12} xs={12}>
                    <AnalyticReportCard
                      name={report.name}
                      isPartnerManaged={report.isPartnerManaged}
                      canClientEdit={report.canClientEdit}
                      editedByClient={report.editedByClient}
                      partnerName={report.partnerName}
                      usesCustomDates={report.usingCustomDates}
                      filterLabels={parseFilterLabels(report.reportJsonValue)}
                      onClick={() => navigate(`/analytics/reports/${report.id}`)}
                    />
                  </Grid>
                )
              })}
              {/* Must be a Grid item: a plain child of a spacing container sits at the
                  container's negative-margin edge, so the box juts left of the page title. */}
              {analyticReports && analyticReports.length === 0 && (
                <Grid item xs={12}>
                  <InformationBox
                    text='You do not have any analytic reports'
                    actionText="Click here to create one"
                    link={`/analytics/reports/add-new-report`}
                    icon={IconName.ErrorOutline}
                  />
                </Grid>
              )}
            </>
          </Grid>
        </LoadingPage>
      )}
    </PageOuterContainer>
  )
}